import { Bell, Circle, CircleCheckBig, CircleDot, X } from "lucide-react";
import SimpleTaskListItem from "@/components/tasks/lists/SimpleTaskListItem/SimpleTaskListItem";
import { DEFAULT_AREA_COLOR, resolveTaskColors } from "@/lib/listColors";
import { formatDurationShort, formatTimeDisplay } from "@/lib/time";
import { deriveTaskLifecycle, getTaskLifecycleLabel } from "@shared/taskLifecycle";

export type SimpleTaskItemTask = {
  id: number;
  title: string;
  date?: string | null;
  notes?: string | null;
  log?: string | null;
  status: "pending" | "done" | "skipped";
  startMinutes?: number | null;
  durationMin?: number | null;
  alertEnabled: boolean;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  barColor?: string | null;
  bgColor?: string | null;
  listColor?: string | null;
  listBgColor?: string | null;
};

function dateToHHMM(d: Date | string | null | undefined): string {
  if (!d) return "";
  const dt = new Date(d);
  if (!Number.isFinite(dt.getTime())) return "";
  return `${dt.getHours().toString().padStart(2, "0")}:${dt
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
}

function resultDurationMinutes(startedAt: Date | string | null | undefined, completedAt: Date | string | null | undefined) {
  if (!startedAt || !completedAt) return 0;
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.round((end - start) / 60000);
}

export default function SimpleTaskItem({
  task: t,
  onClick,
  selected = false,
  dimCompleted = true,
  showDate = false,
}: {
  task: SimpleTaskItemTask;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  selected?: boolean;
  dimCompleted?: boolean;
  showDate?: boolean;
}) {
  const colors = resolveTaskColors({
    barColor: t.barColor,
    bgColor: t.bgColor,
    listColor: t.listColor ?? DEFAULT_AREA_COLOR,
    listBgColor: t.listBgColor ?? null,
  });
  const lifecycle = deriveTaskLifecycle(t);
  const dim = dimCompleted && (lifecycle === "done" || lifecycle === "skipped");
  const scheduledStart =
    lifecycle === "pending" && t.startMinutes !== null && t.startMinutes !== undefined
      ? formatTimeDisplay(t.startMinutes)
      : "";
  const doneStart = lifecycle === "doing" || lifecycle === "done" ? dateToHHMM(t.startedAt) : "";
  const displayTime = scheduledStart || doneStart;
  const resultDuration =
    lifecycle === "done" ? resultDurationMinutes(t.startedAt, t.completedAt) : 0;
  const icon =
    lifecycle === "pending" && t.alertEnabled ? (
      <Bell
        className="alert h-3.5 w-3.5"
        style={{ color: colors.bar }}
        aria-label="Alert on"
      />
    ) : lifecycle === "doing" ? (
      <CircleDot
        className="doing h-3.5 w-3.5"
        style={{ color: colors.bar }}
        aria-label={getTaskLifecycleLabel(lifecycle)}
      />
    ) : lifecycle === "done" ? (
      <CircleCheckBig
        className="done h-3.5 w-3.5"
        style={{ color: colors.bar }}
        aria-label="Done"
      />
    ) : lifecycle === "skipped" ? (
      <X
        className="skipped h-3.5 w-3.5"
        style={{ color: colors.bar }}
        aria-label="Skipped"
      />
    ) : (
      <Circle
        className="pending h-3.5 w-3.5"
        style={{ color: colors.bar }}
        aria-label="Pending"
      />
    );
  const meta = [
    showDate && t.date ? t.date.slice(5) : null,
    displayTime || null,
    resultDuration > 0 ? formatDurationShort(resultDuration) : null,
  ].filter(Boolean).join(" | ");

  return (
    <SimpleTaskListItem
      icon={icon}
      title={
        <span className={t.status === "skipped" ? "line-through" : ""}>
          {t.title}
        </span>
      }
      meta={meta || null}
      selected={selected}
      dimmed={dim}
      color={colors.ink}
      onClick={onClick}
    />
  );
}
